<script setup>
import { computed, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  sections: {
    type: Array,
    required: true,
  },
  activeSectionId: {
    type: String,
    default: null,
  },
  scrollToSection: {
    type: Function,
    required: true,
  },
})

const { t } = useI18n()

const isOpen = ref(false)
let closeTimeout = null

const activeSection = computed(() => {
  return props.sections.find(s => s.id === props.activeSectionId) || props.sections[0]
})

const handleMouseEnter = () => {
  if (closeTimeout) {
    clearTimeout(closeTimeout)
    closeTimeout = null
  }
  isOpen.value = true
}

const handleMouseLeave = () => {
  closeTimeout = setTimeout(() => {
    isOpen.value = false
  }, 150)
}

const handleSectionClick = sectionId => {
  props.scrollToSection(sectionId)
  isOpen.value = false
}

onBeforeUnmount(() => {
  if (closeTimeout) {
    clearTimeout(closeTimeout)
    closeTimeout = null
  }
})
</script>

<template>
  <div
    class="section-navigator relative"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <!-- Trigger -->
    <button
      type="button"
      class="section-trigger flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200"
      :class="{
        'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200': isOpen,
        'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50': !isOpen,
      }"
    >
      <span
        v-if="activeSection?.icon"
        :class="['pi', activeSection.icon, 'text-xs text-primary-500']"
      />
      <span class="truncate max-w-32">{{ t(activeSection?.translationKey || '') }}</span>
      <span
        class="pi pi-chevron-down text-[10px] transition-transform duration-200"
        :class="{ 'rotate-180': isOpen }"
      />
    </button>

    <!-- Dropdown -->
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div
        v-show="isOpen"
        class="section-dropdown absolute top-full left-0 mt-1 min-w-48 py-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50"
      >
        <button
          v-for="section in sections"
          :key="section.id"
          type="button"
          class="section-item w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors duration-150"
          :class="{
            'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300': activeSectionId === section.id,
            'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50': activeSectionId !== section.id,
          }"
          @click="handleSectionClick(section.id)"
        >
          <span
            :class="[
              'pi',
              section.icon,
              'text-xs w-4 text-center',
              activeSectionId === section.id ? 'text-primary-500' : 'text-gray-400 dark:text-gray-500',
            ]"
          />
          <span>{{ t(section.translationKey) }}</span>
          <span
            v-if="activeSectionId === section.id"
            class="ml-auto pi pi-check text-xs text-primary-500"
          />
        </button>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.section-navigator {
  user-select: none;
}

.section-dropdown {
  backdrop-filter: blur(8px);
}
</style>
